import React, { useContext, useMemo } from 'react';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Tag, Tooltip } from 'antd';

import ApiConnectionsContext from '@totem/components/administration/apiConnections/ApiConnectionsContext';
import {
  ApiConnectionStatus,
  ApiConnectionSummary,
  ObjectReference,
} from '@totem/components/administration/apiConnections/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const TYPE_COLORS: Record<string, string> = {
  Acronis: 'blue',
  ConnectWise: 'green',
  NozomiVantage: 'purple',
  // eslint-disable-next-line camelcase
  Tempered_Conductor: 'orange',
  EndOfLife: 'red',
};

const STATUS_COLORS: Record<ApiConnectionStatus, string> = {
  Active: 'success',
  Inactive: 'warning',
  Deleted: 'error',
};

const ApiConnectionsTable = () => {
  const { loading, data, onEdit, onDelete, onStatusChange } = useContext(
    ApiConnectionsContext,
  );

  const typeFilters = useMemo(
    () =>
      Array.from(new Set(data.map((rec) => rec.type)))
        .sort()
        .map((typ) => ({ text: typ, value: typ })),
    [data],
  );

  const orgFilters = useMemo(
    () =>
      Array.from(
        new Map(
          data
            .filter((rec) => rec.organization?.name)
            .map((rec) => [rec.organization.name, rec.organization.name]),
        ).values(),
      )
        .sort()
        .map((name) => ({ text: name, value: name })),
    [data],
  );

  const statusFilters = useMemo(
    () =>
      Array.from(new Set(data.map((rec) => rec.status)))
        .sort()
        .map((sts) => ({ text: sts, value: sts })),
    [data],
  );

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: typeFilters,
      onFilter: (value: boolean | React.Key, record: ApiConnectionSummary) =>
        record.type === value,
      render: (type: string) => (
        <Tag color={TYPE_COLORS[type] ?? 'default'}>{type}</Tag>
      ),
      sorter: (rec1: ApiConnectionSummary, rec2: ApiConnectionSummary) =>
        rec1.type.localeCompare(rec2.type),
    },
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (rec1: ApiConnectionSummary, rec2: ApiConnectionSummary) =>
        rec1.displayName.localeCompare(rec2.displayName),
    },
    {
      title: 'Organization',
      dataIndex: 'organization',
      key: 'organization',
      filters: orgFilters,
      onFilter: (value: boolean | React.Key, record: ApiConnectionSummary) =>
        (record.organization?.name ?? '') === value,
      render: (org: ObjectReference) => org?.name ?? '—',
      sorter: (rec1: ApiConnectionSummary, rec2: ApiConnectionSummary) =>
        (rec1.organization?.name ?? '').localeCompare(
          rec2.organization?.name ?? '',
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: statusFilters,
      onFilter: (value: boolean | React.Key, record: ApiConnectionSummary) =>
        record.status === value,
      render: (status: ApiConnectionStatus) => (
        <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>
      ),
      sorter: (rec1: ApiConnectionSummary, rec2: ApiConnectionSummary) =>
        rec1.status.localeCompare(rec2.status),
    },
    {
      title: 'Created By',
      dataIndex: 'createdByName',
      key: 'createdByName',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val: string) => (val ? new Date(val).toLocaleDateString() : '—'),
      sorter: (rec1: ApiConnectionSummary, rec2: ApiConnectionSummary) =>
        new Date(rec1.createdAt).getTime() - new Date(rec2.createdAt).getTime(),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: ApiConnectionSummary) => {
        const isActive = record.status === 'Active';
        return (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record.id)}
            />
            <Tooltip title={isActive ? 'Deactivate' : 'Activate'}>
              <Button
                size="small"
                onClick={() =>
                  onStatusChange(record.id, isActive ? 'Inactive' : 'Active')
                }
              >
                {isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </Tooltip>
            <Popconfirm
              title="Delete this connection?"
              onConfirm={() => onDelete(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <IBTable
      rowKey="id"
      loading={loading}
      dataSource={data}
      columns={columns}
      pagination={{ defaultPageSize: 20, showSizeChanger: true }}
    />
  );
};

export default ApiConnectionsTable;
